'use client';

import { useMemo } from 'react';
import type { CalendarEvent, EventType } from '@/lib/types';
import { getEventSlug } from '@/lib/event-slugs';
import { getDayName, getMonthName } from '@/lib/constants';

const EVENT_TYPE_COLORS: Partial<Record<EventType, string>> = {
  school_term_start: 'var(--event-festival)',
  exam_period: 'var(--event-holiday)',
  enrollment_deadline: 'var(--event-national)',
  school_term_end: 'var(--event-observance)',
  school_holiday: 'var(--event-event)',
};

interface DayCell {
  dateStr: string;
  day: number;
  month: number;
  events: CalendarEvent[];
  isOutside: boolean; // day falls outside semester range
}

interface WeekRow {
  weekNum: number;
  days: DayCell[];
}

function toDateStr(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function getMonday(d: Date): Date {
  const result = new Date(d);
  const day = result.getDay(); // 0=Sun, 1=Mon...
  const diff = day === 0 ? -6 : 1 - day;
  result.setDate(result.getDate() + diff);
  return result;
}

function getISOWeekNumber(d: Date): number {
  const tmp = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
  tmp.setUTCDate(tmp.getUTCDate() + 4 - (tmp.getUTCDay() || 7));
  const yearStart = new Date(Date.UTC(tmp.getUTCFullYear(), 0, 1));
  return Math.ceil(((tmp.getTime() - yearStart.getTime()) / 86400000 + 1) / 7);
}

function buildWeekRows(semStart: Date, semEnd: Date, events: CalendarEvent[]): WeekRow[] {
  const rows: WeekRow[] = [];
  const cursor = getMonday(semStart);
  const semStartStr = toDateStr(semStart);
  const semEndStr = toDateStr(semEnd);

  while (cursor <= semEnd) {
    const weekNum = getISOWeekNumber(cursor);
    const days: DayCell[] = [];

    for (let i = 0; i < 7; i++) {
      const current = new Date(cursor);
      current.setDate(cursor.getDate() + i);
      const dateStr = toDateStr(current);
      const isOutside = dateStr < semStartStr || dateStr > semEndStr;

      const dayEvents = events.filter(
        (e) => e.dateStart <= dateStr && e.dateEnd >= dateStr
      );

      days.push({
        dateStr,
        day: current.getDate(),
        month: current.getMonth(),
        events: dayEvents,
        isOutside,
      });
    }

    rows.push({ weekNum, days });
    cursor.setDate(cursor.getDate() + 7);
  }

  return rows;
}

function EventChip({ event }: { event: CalendarEvent }) {
  const bg = EVENT_TYPE_COLORS[event.type] || event.color;
  return (
    <a
      href={`/events/${getEventSlug(event.id)}`}
      className="mt-0.5 block truncate rounded px-1 py-px text-caption leading-tight font-medium text-text-on-brand hover:underline"
      style={{ backgroundColor: bg }}
      title={event.title}
    >
      {event.title}
    </a>
  );
}

/** Mon-indexed day headers for the week view (Mon=1, ..., Sun=0). */
const MON_INDEXED_DAYS = [1, 2, 3, 4, 5, 6, 0] as const;

function getShortMonth(monthIndex: number, lang?: string): string {
  return getMonthName(monthIndex, lang).slice(0, 3);
}

function SemesterGrid({ title, rows, lang }: { title: string; rows: WeekRow[]; lang?: string }) {
  return (
    <section className="mb-8">
      <h4 className="font-heading mb-3 text-base font-bold text-text-primary md:text-lg">
        {title}
      </h4>
      <div className="overflow-x-auto rounded-xl border border-border-subtle">
        <table className="min-w-[640px] w-full border-collapse">
          <thead>
            <tr className="bg-surface-card text-label font-semibold uppercase tracking-wider text-text-secondary">
              <th className="w-10 border-b border-border-subtle px-1 py-2 text-center">Wk</th>
              {MON_INDEXED_DAYS.map((dayIdx) => (
                <th key={dayIdx} className="min-w-[80px] border-b border-border-subtle px-1 py-2 text-center">
                  {getDayName(dayIdx, lang)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.weekNum} className="border-b border-border-subtle last:border-b-0">
                <td className="w-10 px-1 py-1 text-center text-caption font-medium tabular-nums text-text-dim">
                  {row.weekNum}
                </td>
                {row.days.map((cell) => (
                  <td
                    key={cell.dateStr}
                    className={`min-w-[80px] border-l border-border-subtle px-1 py-1 align-top ${
                      cell.isOutside ? 'bg-surface-base/50' : ''
                    }`}
                  >
                    <span
                      className={`text-label tabular-nums ${
                        cell.isOutside
                          ? 'text-text-dim'
                          : cell.events.length > 0
                            ? 'font-semibold text-text-primary'
                            : 'text-text-secondary'
                      }`}
                    >
                      {cell.day === 1 ? `${getShortMonth(cell.month, lang)} ` : ''}
                      {cell.day}
                    </span>
                    {cell.events.map((e) => (
                      <EventChip key={e.id} event={e} />
                    ))}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

interface SemesterWeekViewProps {
  events: CalendarEvent[];
  year: number;
  lang?: string;
}

export function SemesterWeekView({ events, year, lang }: SemesterWeekViewProps) {
  const { oddRows, evenRows } = useMemo(() => {
    // Odd semester: Jul 1 – Dec 31 of year
    const oddStart = new Date(year, 6, 1);
    const oddEnd = new Date(year, 11, 31);
    // Even semester: Jan 1 – Jun 30 of year+1
    const evenStart = new Date(year + 1, 0, 1);
    const evenEnd = new Date(year + 1, 5, 30);

    return {
      oddRows: buildWeekRows(oddStart, oddEnd, events),
      evenRows: buildWeekRows(evenStart, evenEnd, events),
    };
  }, [events, year]);

  return (
    <div>
      <SemesterGrid
        title={`Odd Semester (Jul – Dec ${year})`}
        rows={oddRows}
        lang={lang}
      />
      <SemesterGrid
        title={`Even Semester (Jan – Jun ${year + 1})`}
        rows={evenRows}
        lang={lang}
      />
    </div>
  );
}
